import debounce from "../../utils/debounce";

declare global {
  interface HTMLElementTagNameMap {
    "swym-wishlist": SwymWishlist;
  }

  interface Window {
    SwymCallbacks?: { push: (callback: SwymCallback) => void };
    SwymPageData?: SwymProductData;
    SwymTracker?: {
      EventTypes?: {
        addToWishList?: unknown;
      };
    };
    _swat?: SwymInstance;
    // Set by snippets/swym-wishlist.liquid once the tracker has booted.
    swymTracker?: SwymInstance;
  }
}

type SwymProductData = Record<string, unknown>;

type SwymInstance = {
  initializeActionButtons?: (selector: string) => void;
  addToWishList?: (product: SwymProductData, callback?: () => void) => void;
};

type SwymCallback = (swat: SwymInstance) => void;

const BUTTON_SELECTOR = ".swym-button";

// Swym marks a button it has taken ownership of with `swym-loaded`, and the
// theme keeps EPI buttons hidden until then — see the `swym-product-view-defaults`
// style in snippets/swymSnippet.liquid and `.product-header .swym-button` in
// assets/theme.css. A button Swym never initialised is therefore an invisible
// button, which is why we re-run its initialiser rather than wait for it.
const LOADED_CLASS = "swym-loaded";
const PENDING_BUTTON_SELECTOR = `${BUTTON_SELECTOR}[data-with-epi]:not(.${LOADED_CLASS})`;
const DEFAULT_CONTAINER_SELECTOR = "body";
const REINITIALIZE_DELAY = 100;

const TOOLTIP_CLASS = "login-hover";
const TOOLTIP_SHOWN_CLASS = "show";

// How long the tooltip stays visible on a touch tap before the visitor is
// redirected to login — long enough to read a short hint, short enough that
// the tap still feels like it did something.
const TOUCH_REDIRECT_DELAY = 1200;

// Distance kept between the button and the tooltip, and between the tooltip
// and the viewport edges when it has to be nudged back into view.
const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 12;

class LoginTooltip {
  private element: HTMLElement | null = null;
  private anchor: HTMLElement | null = null;
  private text = "";
  private readonly repositionHandler: () => void;

  constructor() {
    this.repositionHandler = this.reposition.bind(this);
  }

  setText(text: string): void {
    this.text = text;
    if (this.element) this.element.textContent = text;
  }

  show(anchor: HTMLElement): void {
    const element = this.getElement();
    this.anchor = anchor;
    this.position(element, anchor);
    element.classList.add(TOOLTIP_SHOWN_CLASS);

    window.addEventListener("scroll", this.repositionHandler, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", this.repositionHandler, {
      passive: true,
    });
  }

  hide(): void {
    this.anchor = null;
    this.element?.classList.remove(TOOLTIP_SHOWN_CLASS);

    window.removeEventListener("scroll", this.repositionHandler, {
      capture: true,
    });
    window.removeEventListener("resize", this.repositionHandler);
  }

  isAnchoredTo(element: HTMLElement | null): boolean {
    return this.anchor !== null && this.anchor === element;
  }

  private getElement(): HTMLElement {
    if (this.element?.isConnected) return this.element;

    this.element =
      document.querySelector<HTMLElement>(`.${TOOLTIP_CLASS}`) ??
      document.createElement("div");

    if (!this.element.isConnected) {
      this.element.className = TOOLTIP_CLASS;
      this.element.textContent = this.text;
      document.body.appendChild(this.element);
    }

    return this.element;
  }

  private reposition(): void {
    if (!this.element || !this.anchor) return;
    this.position(this.element, this.anchor);
  }

  // Places the tooltip under the anchor, flipping it above and clamping it
  // horizontally whenever it would otherwise be rendered outside the viewport.
  private position(element: HTMLElement, anchor: HTMLElement): void {
    const anchorRect = anchor.getBoundingClientRect();
    const { width, height } = element.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    let top = anchorRect.bottom + TOOLTIP_GAP;
    if (top + height > viewportHeight - VIEWPORT_PADDING) {
      const above = anchorRect.top - TOOLTIP_GAP - height;
      top =
        above >= VIEWPORT_PADDING
          ? above
          : Math.max(
              VIEWPORT_PADDING,
              viewportHeight - VIEWPORT_PADDING - height,
            );
    }

    const centered = anchorRect.left + anchorRect.width / 2 - width / 2;
    const maxLeft = Math.max(
      VIEWPORT_PADDING,
      viewportWidth - VIEWPORT_PADDING - width,
    );
    const left = Math.min(Math.max(VIEWPORT_PADDING, centered), maxLeft);

    element.style.top = `${Math.round(top)}px`;
    element.style.left = `${Math.round(left)}px`;
  }
}

// Touch browsers fire a synthetic `mouseover` immediately before `click`, so
// on a touchscreen the tooltip would otherwise be shown and then immediately
// hidden (by navigation) within the same tap, never giving the visitor a
// chance to read it. Media query rather than UA sniffing, and re-checked per
// click rather than cached, so it still tracks reality if input changes
// (e.g. a touchscreen laptop switching between trackpad and touch).
const NO_HOVER_QUERY = "(hover: none)";

class SwymWishlist extends HTMLElement {
  private readonly tooltip = new LoginTooltip();
  private swat: SwymInstance | null = null;
  private observer: MutationObserver | null = null;
  // Set while a touch tap's redirect is pending, so it can be cancelled if
  // the element disconnects before the timer fires.
  private redirectTimeoutId: number | null = null;

  private readonly refreshButtons = debounce(
    () => this.initializeButtons(),
    REINITIALIZE_DELAY,
  );

  connectedCallback(): void {
    this.tooltip.setText(this.getAttribute("tooltip-text") ?? "");

    // Delegated so buttons Swym or a section re-render adds later are covered.
    document.addEventListener("mouseover", this.onMouseOver);
    document.addEventListener("mouseout", this.onMouseOut);
    // Capture phase: when the visitor is signed out this handler has to stop
    // the click before Swym's own listener on the button sees it.
    document.addEventListener("click", this.onClick, true);

    document.addEventListener("swym:ready", this.onSwymReady);
    document.addEventListener("swym:collections-loaded", this.refreshButtons);
    document.addEventListener("shopify:section:load", this.refreshButtons);

    this.observeAddedButtons();

    // The tracker may already have booted before this bundle ran; the snippet
    // records that on `window.swymTracker`. The SwymCallbacks push is only a
    // backstop for pages rendered without snippets/swym-wishlist.liquid.
    if (window.swymTracker ?? window._swat) {
      this.onSwymReady();
    } else {
      window.SwymCallbacks?.push(() => this.onSwymReady());
    }
  }

  disconnectedCallback(): void {
    document.removeEventListener("mouseover", this.onMouseOver);
    document.removeEventListener("mouseout", this.onMouseOut);
    document.removeEventListener("click", this.onClick, true);

    document.removeEventListener("swym:ready", this.onSwymReady);
    document.removeEventListener(
      "swym:collections-loaded",
      this.refreshButtons,
    );
    document.removeEventListener("shopify:section:load", this.refreshButtons);

    this.observer?.disconnect();
    this.observer = null;
    if (this.redirectTimeoutId !== null) {
      window.clearTimeout(this.redirectTimeoutId);
      this.redirectTimeoutId = null;
    }
    this.tooltip.hide();
  }

  // Rendered by Liquid on every request, so it stays correct even though the
  // bundle around it is served from cache.
  private get isLoggedIn(): boolean {
    return this.hasAttribute("logged-in");
  }

  private get loginUrl(): string {
    return this.getAttribute("login-url") || "/account/login";
  }

  private get containerSelector(): string {
    return (
      this.getAttribute("container-selector") || DEFAULT_CONTAINER_SELECTOR
    );
  }

  private onSwymReady = (): void => {
    const swat = window.swymTracker ?? window._swat ?? null;
    if (!swat || this.swat === swat) return;

    this.swat = swat;
    this.initializeButtons();
  };

  // Swym's own auto-init only sees the buttons present when it boots, so any
  // button rendered by a section reload, a filtered collection or a product
  // recommendation stays uninitialised — and therefore hidden.
  private initializeButtons(): void {
    if (!this.swat?.initializeActionButtons) return;
    if (!document.querySelector(PENDING_BUTTON_SELECTOR)) return;
    if (!document.querySelector(this.containerSelector)) return;

    this.swat.initializeActionButtons(this.containerSelector);
  }

  private observeAddedButtons(): void {
    this.observer = new MutationObserver((mutations) => {
      const hasNewButton = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches(PENDING_BUTTON_SELECTOR) ||
              node.querySelector(PENDING_BUTTON_SELECTOR) !== null),
        ),
      );

      if (hasNewButton) this.refreshButtons();
    });

    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  private onMouseOver = (event: MouseEvent): void => {
    if (this.isLoggedIn) return;

    const button = this.closestButton(event.target);
    if (!button || this.tooltip.isAnchoredTo(button)) return;

    this.tooltip.show(button);
  };

  private onMouseOut = (event: MouseEvent): void => {
    const button = this.closestButton(event.target);
    if (!button || !this.tooltip.isAnchoredTo(button)) return;

    const related = event.relatedTarget;
    if (related instanceof Node && button.contains(related)) return;

    this.tooltip.hide();
  };

  private onClick = (event: MouseEvent): void => {
    const button = this.closestButton(event.target);
    if (!button) return;

    // These buttons sit inside the product form and the product card, so the
    // browser default (submit / follow) is never what we want.
    event.preventDefault();

    if (!this.isLoggedIn) {
      event.stopPropagation();

      if (this.isTouchDevice()) {
        this.tooltip.show(button);
        if (this.redirectTimeoutId !== null) {
          window.clearTimeout(this.redirectTimeoutId);
        }
        this.redirectTimeoutId = window.setTimeout(() => {
          window.location.href = this.loginUrl;
        }, TOUCH_REDIRECT_DELAY);
        return;
      }

      this.tooltip.hide();
      window.location.href = this.loginUrl;
      return;
    }

    // Swym owns any button it initialised and already knows which variant the
    // button belongs to — let its handler run. We only step in for buttons it
    // never claimed, which otherwise do nothing at all when clicked.
    if (button.classList.contains(LOADED_CLASS)) return;

    this.addToWishlist(button);
  };

  private isTouchDevice(): boolean {
    return window.matchMedia?.(NO_HOVER_QUERY).matches ?? false;
  }

  private closestButton(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    return target.closest<HTMLElement>(BUTTON_SELECTOR);
  }

  private addToWishlist(button: HTMLElement): void {
    const swat = this.swat ?? window._swat ?? null;
    if (!swat?.addToWishList) return;

    const product = this.productFromButton(button) ?? this.productFromPage();
    if (!product) return;

    swat.addToWishList(product);
  }

  // Preferred source: the button carries the product it was rendered for, so a
  // heart on a "You may also like" card adds that card's product rather than
  // whatever product the page itself is about.
  private productFromButton(button: HTMLElement): SwymProductData | null {
    const empi = parseId(button.dataset.productId);
    const epi = parseId(button.dataset.variantId);
    const du = button.dataset.productUrl;

    if (empi === null || epi === null || !du) return null;

    return { et: addToWishListEventType(), empi, epi, du };
  }

  private productFromPage(): SwymProductData | null {
    const pageData = window.SwymPageData;
    if (!pageData) return null;

    return {
      ...(JSON.parse(JSON.stringify(pageData)) as SwymProductData),
      et: addToWishListEventType(),
    };
  }
}

function parseId(value: string | undefined): number | null {
  if (!value) return null;

  // The Liquid markup pipes ids through `| json`, so strip any stray quoting.
  const id = Number(value.replace(/"/g, "").trim());
  return Number.isFinite(id) && id > 0 ? id : null;
}

function addToWishListEventType(): unknown {
  return window.SwymTracker?.EventTypes?.addToWishList;
}

if (!customElements.get("swym-wishlist")) {
  customElements.define("swym-wishlist", SwymWishlist);
}

export { SwymWishlist };
